import { Col } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import { useContext, useEffect } from "react";
import { FormattedMessage } from "react-intl";

import { VerificationStatus } from "@/apis/IdentityCheckHelper";
import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";
import { STEP_ID } from "@/components/constants";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

export const DATA_TEST_ID_VERIFICATION_PENDING = "VerificationPendingView";

/** Use a static wait time for MLP */
export const MIN_ESTIMATED_WAIT_TIME = 10;
export const MAX_ESTIMATED_WAIT_TIME = 30;

/**
 * A view that shows an estimated wait time needed to review NH's submitted documents. A static wait time is used for
 * MLP.
 */
export default function VerificationPending(): JSX.Element {
  const { state, dispatch } = useContext(AppContext);

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    switch (state.docsVerificationStatus) {
      case VerificationStatus.LIGHT_PENDING: {
        dispatch({
          type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
          chatSupportRefPage: "ezo-identity-check-manual-lightweight-wait",
        });
        break;
      }
      case VerificationStatus.FULL_PENDING: {
        dispatch({
          type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
          chatSupportRefPage: "ezo-identity-check-manual-full-wait",
        });
        break;
      }
      default:
        // do nothing
        break;
    }
  }, [state.docsVerificationStatus]);

  /**
   * Display next button when the PENDING verification view is rendered.
   */
  useEffect(() => {
    dispatch({
      stepId: STEP_ID.IDENTITY_CHECK,
      type: ActionType.SET_HIDE_NEXT,
      hideNext: false,
    });

    switch (state.docsVerificationStatus) {
      case VerificationStatus.LIGHT_PENDING: {
        trackStructEvent({
          category: Day1EventCategory.IDCheckLightWait,
          action: Day1EventAction.DisplayWaitUI,
        });
        dispatch({
          type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
          nonDirectedChatSupportContext: "ON_LIGHT_RIV_WAIT_STEP",
        });
        break;
      }
      case VerificationStatus.FULL_PENDING: {
        trackStructEvent({
          category: Day1EventCategory.IDCheckFullWait,
          action: Day1EventAction.DisplayWaitUI,
        });

        dispatch({
          type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
          nonDirectedChatSupportContext: "ON_FULL_RIV_WAIT_STEP",
        });
        break;
      }
      default:
        // do nothing
        break;
    }
  }, [dispatch]);

  return (
    <div data-test-id={DATA_TEST_ID_VERIFICATION_PENDING}>
      <Col gridGap="S400">
        <StepBodyStyledText>
          <Text
            dataTestId="pleaseWaitTxt"
            fontWeight="regular"
            textAlign="center"
          >
            <FormattedMessage id="identityCheck-pending-pleaseWait" />
          </Text>
        </StepBodyStyledText>
        <Col>
          <StepBodyStyledText>
            <Text
              dataTestId="estimatedWaitTime"
              fontWeight="medium"
              textAlign="center"
            >
              <FormattedMessage
                id="identityCheck-pending-estimatedWaitTime"
                values={{
                  minTimeMin: MIN_ESTIMATED_WAIT_TIME,
                  maxTimeMin: MAX_ESTIMATED_WAIT_TIME,
                }}
              />
            </Text>
          </StepBodyStyledText>
        </Col>
      </Col>
    </div>
  );
}
